import { QuestionTypeMultiChoice, QuestionTypeSingleChoice, type Question, type QuestionOption, type QuestionSelection } from '@agion/mission-stream'

/**
 * A choice's label and description as the server states them
 * (missionstream.HITLOptionDetail on mission.hitl.request): what a question
 * shows under each option (persona walkthrough F-CHAT-06). The answer is
 * still the option string itself, which the detail's id names.
 */
export interface QuestionOptionDetail {
  /** The option this describes. */
  id: string
  label: string
  description: string
}

/** The well-formed option details a question row or request carries, or undefined for none. */
export function questionOptionDetails(value: unknown): QuestionOptionDetail[] | undefined {
  if (!Array.isArray(value)) return undefined
  const details = value.filter(
    (item): item is QuestionOptionDetail =>
      !!item &&
      typeof item === 'object' &&
      typeof (item as QuestionOptionDetail).id === 'string' &&
      typeof (item as QuestionOptionDetail).label === 'string' &&
      typeof (item as QuestionOptionDetail).description === 'string',
  )
  return details.length > 0 ? details : undefined
}

/** A question a person picks options in: the generated Question with what such a question states. */
export type ChoiceQuestion = Question & { options: QuestionOption[]; selection: QuestionSelection }

/**
 * The structured question a frame or stored row carries, when it is a single
 * or multi choice with its options and numbers (agion/Agion#3559). Any other
 * question, and a row with none, draws as it did.
 */
export function choiceQuestion(value: unknown): ChoiceQuestion | undefined {
  if (!value || typeof value !== 'object') return undefined
  const question = value as Question
  if (question.type !== QuestionTypeSingleChoice && question.type !== QuestionTypeMultiChoice) return undefined
  if (!Array.isArray(question.options) || question.options.length === 0) return undefined
  if (typeof question.selection?.min !== 'number' || typeof question.selection.max !== 'number') return undefined
  return question as ChoiceQuestion
}

/** What useHITLCountdown runs on: a start and a length, in milliseconds. */
export interface QuestionCountdown {
  from: number
  lastsMs?: number
}

/**
 * A question's countdown. A structured question ends at its expires_at, which
 * a replay does not restart, and the runner's wait can outlast timeout_ms (the
 * time before its nudge). One that is not open counts nothing down: it stays
 * drawn as it was asked. Any other runs on receivedAt and timeoutMs.
 */
export function questionCountdown(
  request: { receivedAt: number; timeoutMs?: number; question?: ChoiceQuestion },
  isOpen = true,
): QuestionCountdown {
  if (!request.question) return { from: request.receivedAt, lastsMs: request.timeoutMs }
  const expiresAt = Date.parse(request.question.expires_at)
  return isOpen && Number.isFinite(expiresAt) ? { from: 0, lastsMs: expiresAt } : { from: request.receivedAt }
}
